/**
 * TerminalNode — the START and END sinks of a workflow graph.
 *
 * The engine's serialize() derives these from the entry point + the 'END' route
 * sentinel (BPMN / LangGraph `__start__`/`__end__` convention) so every graph
 * shows where flow enters and terminates. Rendered as a small rounded pill,
 * visually distinct from the action (WorkflowNode) and decision (DecisionNode)
 * boxes. Carries both a top target + bottom source handle so edges connect
 * regardless of which terminal it is.
 */
import { Handle, Position } from 'reactflow';
import { IonIcon } from '@ionic/react';
import { playOutline, flagOutline, timeOutline, flashOutline, handLeftOutline } from 'ionicons/icons';
import './TerminalNode.css';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Lightweight cron → human label for the common shapes a schedule uses; falls
// back to the raw expression for anything exotic (never throws). 5-field cron:
// `min hour dom mon dow`.
const humanCron = (cron) => {
  if (!cron || typeof cron !== 'string') return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return cron;
  const [min, hour, dom, mon, dow] = parts;
  const everyMin = min.match(/^\*\/(\d+)$/);
  if (everyMin && hour === '*' && dom === '*' && mon === '*' && dow === '*') return `Every ${everyMin[1]}m`;
  if (min === '0' && hour === '*' && dom === '*' && mon === '*' && dow === '*') return 'Hourly';
  // Fixed time-of-day cases
  const hh = String(hour).padStart(2, '0');
  const mm = String(min).padStart(2, '0');
  const timeOk = /^\d{1,2}$/.test(hour) && /^\d{1,2}$/.test(min);
  if (timeOk && dom === '*' && mon === '*') {
    if (dow === '*') return `Daily ${hh}:${mm}`;
    if (dow === '1-5') return `${hh}:${mm} Mon–Fri`;
    const days = dow.split(',').map((d) => DOW[Number(d)] ?? d).filter(Boolean);
    if (days.length) return `${hh}:${mm} ${days.join(',')}`;
  }
  return cron;
};

// Build the trigger badges for the Start node from its injected `triggers`
// summary: { cron, timezone, webhook }. Cron + webhook can BOTH show (dual-mode
// agents). Nothing configured → a single "Manual" badge.
const buildTriggerBadges = (triggers) => {
  if (!triggers) return [];
  const badges = [];
  if (triggers.cron) {
    badges.push({ key: 'cron', icon: timeOutline, label: humanCron(triggers.cron) || 'Cron' });
  }
  if (triggers.webhook) {
    badges.push({ key: 'webhook', icon: flashOutline, label: 'Webhook' });
  }
  if (!badges.length) {
    badges.push({ key: 'manual', icon: handLeftOutline, label: 'Manual' });
  }
  return badges;
};

export const TerminalNode = ({ data, isConnectable }) => {
  const variant = data?.terminal === 'start' ? 'start' : 'end';
  const label = data?.label || (variant === 'start' ? 'Start' : 'End');
  // Distinct shapes: a play triangle to GO, a finish flag to END.
  const icon = variant === 'start' ? playOutline : flagOutline;
  // Trigger badges hang under the Start pill (how this agent fires).
  const triggerBadges = variant === 'start' ? buildTriggerBadges(data?.triggers) : [];

  return (
    <div className={`gwe-terminal-node gwe-terminal-node--${variant}`}>
      {/* Handles for both orientations: left/top = target (END), right/bottom =
          source (START). The active layout picks which side via the edge's
          sourceHandle/targetHandle. */}
      <Handle type="target" position={Position.Left} id="target-left" isConnectable={isConnectable} className="gwe-terminal-node__handle" />
      <Handle type="target" position={Position.Top} id="top" isConnectable={isConnectable} className="gwe-terminal-node__handle" />
      <span className="gwe-terminal-node__dot">
        <IonIcon icon={icon} />
      </span>
      <span className="gwe-terminal-node__label">{label}</span>
      {triggerBadges.length > 0 && (
        <div className="gwe-terminal-node__triggers">
          {triggerBadges.map((b) => (
            <span key={b.key} className={`gwe-trigger-badge gwe-trigger-badge--${b.key}`}>
              <IonIcon icon={b.icon} />
              <span className="gwe-trigger-badge__label">{b.label}</span>
            </span>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Right} id="right" isConnectable={isConnectable} className="gwe-terminal-node__handle" />
      <Handle type="source" position={Position.Top} id="source-top" isConnectable={isConnectable} className="gwe-terminal-node__handle" />
      <Handle type="source" position={Position.Bottom} id="bottom" isConnectable={isConnectable} className="gwe-terminal-node__handle" />
    </div>
  );
};

export default TerminalNode;
